import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SeoHead from '@/components/base/SeoHead';
import { resetPasswordWithToken } from '@/lib/api/profiles';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      setError('This reset link is invalid or incomplete.');
      return;
    }

    const form = event.currentTarget;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    const confirmPassword = (form.elements.namedItem('confirm_password') as HTMLInputElement).value;

    if (password.length < 8) {
      setError('Please choose a password with at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');
    setNotice('');

    try {
      await resetPasswordWithToken(token, password);
      setNotice('Password updated. You can sign in now.');
      setTimeout(() => navigate('/dealer', { replace: true }), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center px-6">
      <SeoHead
        title="Reset Password | Stingray"
        description="Create a new password for your Stingray account."
      />
      <div className="w-full max-w-md bg-[#1C2B3A] border border-white/10 rounded-2xl p-8 text-center">
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-white mb-4"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          <i className={`${error ? 'ri-error-warning-line' : notice ? 'ri-check-line' : 'ri-lock-password-line'} text-2xl`}></i>
        </div>
        <h1 className="text-xl font-black text-white mb-2">
          {error ? 'Password reset issue' : notice ? 'Password updated' : 'Set a new password'}
        </h1>
        <p className="text-sm text-white/60 mb-6">
          {error || notice || 'Enter your new password below to finish resetting your account.'}
        </p>

        {!notice && (
          <form onSubmit={handleSubmit} className="space-y-4 text-left">
            <div>
              <label className="block text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">
                New Password
              </label>
              <input
                type="password"
                name="password"
                required
                minLength={8}
                placeholder="Minimum 8 characters"
                className="w-full bg-[#0D1B2A] border border-white/10 text-white text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#F97316] placeholder-white/20 transition-colors"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirm_password"
                required
                minLength={8}
                placeholder="Repeat your password"
                className="w-full bg-[#0D1B2A] border border-white/10 text-white text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#F97316] placeholder-white/20 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-bold text-white disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}></i>
              {loading ? 'Saving...' : 'Update Password'}
            </button>
          </form>
        )}

        {(error || !token || notice) && (
          <Link
            to="/dealer"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white mt-6"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dealer Login
          </Link>
        )}
      </div>
    </div>
  );
}
